import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import T2TTopNav from "../Layout/T2TTopNav";
import { getData } from "../../../FetchApiServices";
import { useToast } from "../Layout/T2TUIProvider";
import {
  fetchIntegrationHealth,
  fetchIntegrationCallLog,
  retryIntegrationCall,
  fetchIntegrationCostTracker,
} from "../../../api/t2tIntegrationApi";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const COLORS = ["#36B82A", "#2563EB", "#D97706", "#DC2626", "#7C3AED", "#0F766E", "#DB2777", "#1B3A6B"];
const STATUS_COLORS = { approved: "#36B82A", in_progress: "#2563EB", rejected: "#DC2626", withdrawn: "#94A3B8", on_hold: "#D97706", pending: "#7C3AED" };

const T2TReports = () => {
  const toast = useToast();
  // BUG-34: support deep-linking from Team Overview via ?tab=tracker
  const initialTab = (typeof window !== "undefined")
    ? (new URLSearchParams(window.location.search).get("tab") || "overview")
    : "overview";
  const [activeTab, setActiveTab] = useState(initialTab);

  const TABS = [
    // BUG-35: renamed from "Overview" to "Executive Dashboard" so the CXO
    // intent is unambiguous (separate from My Queue → Team Overview).
    { key: "overview",     label: "Executive Dashboard" },
    { key: "tracker",      label: "Request Tracker" },
    { key: "sla",          label: "SLA Performance" },
    { key: "analytics",    label: "Workflow Analytics" },
    { key: "users",        label: "User Performance" },
    { key: "integrations", label: "Integration Health" },  // BUG-31
  ];

  return (
    <>
      <T2TTopNav title="Reports" />
      <div className="t2t-content">
        <div className="t2t-page-title">Reports</div>
        <p className="t2t-page-sub">Management dashboards, analytics and performance tracking.</p>

        <div className="t2t-tab-group" style={{ marginBottom: 20 }}>
          {TABS.map((tab) => (
            <button key={tab.key} className={`t2t-tab${activeTab === tab.key ? " active" : ""}`} onClick={() => setActiveTab(tab.key)}>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "overview"     && <OverviewTab toast={toast} />}
        {activeTab === "tracker"      && <TrackerTab />}
        {activeTab === "sla"          && <SLATab />}
        {activeTab === "analytics"    && <AnalyticsTab />}
        {activeTab === "users"        && <UsersTab toast={toast} />}
        {activeTab === "integrations" && <IntegrationHealthTab toast={toast} />}
      </div>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE 1 — Overview Dashboard
// ═══════════════════════════════════════════════════════════════════════════════
const OverviewTab = ({ toast }) => {
  const [metrics, setMetrics]         = useState(null);
  const [byWorkflow, setByWorkflow]   = useState([]);
  const [dailyVolume, setDailyVolume] = useState([]);
  const [byStatus, setByStatus]       = useState([]);
  const [bottlenecks, setBottlenecks] = useState([]);
  const [loading, setLoading]         = useState(false);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const [mResp, wResp, dResp, sResp, bResp] = await Promise.all([
        getData("t2t/reports/overview/metrics"),
        getData("t2t/reports/overview/by-workflow"),
        getData("t2t/reports/overview/daily-volume"),
        getData("t2t/reports/overview/by-status"),
        getData("t2t/reports/overview/bottlenecks"),
      ]);
      if (!mResp.error) setMetrics(mResp.data?.data || {});
      if (!wResp.error) setByWorkflow(wResp.data?.data || []);
      if (!dResp.error) setDailyVolume(dResp.data?.data || []);
      if (!sResp.error) setByStatus(sResp.data?.data || []);
      if (!bResp.error) setBottlenecks(bResp.data?.data || []);
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: "var(--t2t-text-muted)", fontSize: 13 }}>Loading dashboard...</div>;

  return (
    <>
      {/* Metric Cards */}
      <div className="t2t-stats-grid" style={{ marginBottom: 24 }}>
        {[
          { label: "Active Requests", value: metrics?.active_requests ?? "—", color: "#2563EB" },
          { label: "Pending My Action", value: metrics?.pending_my_action ?? "—", color: "#D97706" },
          { label: "Overdue / SLA Breached", value: metrics?.overdue ?? "—", color: "#DC2626" },
          { label: "Completed This Month", value: metrics?.completed_this_month ?? "—", color: "#36B82A" },
          { label: "Rejection Rate (30d)", value: metrics?.rejection_rate != null ? `${metrics.rejection_rate}%` : "—", color: "#7C3AED" },
        ].map((m) => (
          <div className="t2t-stat-card" key={m.label}>
            <div className="t2t-stat-label">{m.label}</div>
            <div className="t2t-stat-value" style={{ color: m.color }}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Bar — Requests by Workflow */}
        <div className="t2t-card" style={{ padding: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Requests by Workflow (30d)</div>
          {byWorkflow.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={byWorkflow}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="workflow_name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="approved" stackId="a" fill="#36B82A" />
                <Bar dataKey="rejected" stackId="a" fill="#DC2626" />
                <Bar dataKey="in_progress" stackId="a" fill="#2563EB" />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </div>

        {/* Line — Daily Volume */}
        <div className="t2t-card" style={{ padding: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Daily Request Volume (30d)</div>
          {dailyVolume.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={dailyVolume}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </div>

        {/* Donut — By Status */}
        <div className="t2t-card" style={{ padding: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Requests by Status</div>
          {byStatus.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={byStatus} dataKey="count" nameKey="status" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} label={({ status, count }) => `${status} (${count})`}>
                  {byStatus.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.status] || COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </div>

        {/* Horizontal Bar — Bottleneck Stages */}
        <div className="t2t-card" style={{ padding: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Top 5 Bottleneck Stages</div>
          {bottlenecks.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={bottlenecks} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="stage_name" type="category" width={120} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="avg_minutes" fill="#D97706" />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </div>
      </div>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE 2 — Request Tracker
// ═══════════════════════════════════════════════════════════════════════════════
const TrackerTab = () => {
  const navigate = useNavigate();              // BUG-28: row click → request detail
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(false);
  // BUG-29 — 5 new filter slots.
  // BUG-34: honour ?status=in_progress when deep-linked from Team Overview.
  // BUG-35 follow-up: honour ?sla=breached when deep-linked from Queue snapshot.
  const urlParams = (typeof window !== "undefined")
    ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const urlStatus = urlParams.get("status") || "";
  const urlSla    = urlParams.get("sla")    || "";
  const [filters, setFilters]   = useState({
    status: urlStatus, workflow: "", from: "", to: "", search: "",
    priority: "", stage_id: "", department_id: "", submitted_by_name: "", sla: urlSla,
  });
  const [sortBy, setSortBy]     = useState("submitted_on");
  const [sortDir, setSortDir]   = useState("desc");
  // BUG-29 — lookup data for filter pickers
  const [workflows, setWorkflows]     = useState([]);
  const [stages, setStages]           = useState([]);   // refreshed when workflow changes
  const [departments, setDepartments] = useState([]);

  useEffect(() => { fetchTrackerData(); }, [filters, sortBy, sortDir]);
  useEffect(() => { loadWorkflows(); loadDepartments(); }, []);
  useEffect(() => {
    if (filters.workflow) loadStages(filters.workflow);
    else { setStages([]); }
  }, [filters.workflow]);

  const loadWorkflows = async () => {
    const resp = await getData("t2t/workflows?page=1&limit=200&active=true");
    if (resp.error) return;
    const raw = resp.data?.data;
    const list = Array.isArray(raw) ? raw
      : Array.isArray(raw?.rows) ? raw.rows
      : Array.isArray(raw?.data) ? raw.data
      : Array.isArray(raw?.workflows) ? raw.workflows
      : [];
    setWorkflows(list);
  };
  const loadStages = async (workflowId) => {
    const resp = await getData(`t2t/workflows/${workflowId}`);
    if (resp.error) { setStages([]); return; }
    const wf = resp.data?.data || resp.data || {};
    const list = (wf.stages || []).filter((s) => !s.parent_stage_id);
    setStages(list);
  };
  const loadDepartments = async () => {
    const resp = await getData("t2t/org-units");
    if (resp.error) return;
    const raw = resp.data?.data;
    const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.rows) ? raw.rows : []);
    setDepartments(list.filter((o) => o.type === "Department" || o.type === "department"));
  };

  const fetchTrackerData = async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: 1, limit: 100, sort_by: sortBy, sort_dir: sortDir });
    if (filters.status)            params.set("status", filters.status);
    if (filters.workflow)          params.set("workflow", filters.workflow);
    if (filters.from)              params.set("from", filters.from);
    if (filters.to)                params.set("to", filters.to);
    if (filters.search)            params.set("search", filters.search);
    if (filters.priority)          params.set("priority", filters.priority);
    if (filters.stage_id)          params.set("stage_id", filters.stage_id);
    if (filters.department_id)     params.set("department_id", filters.department_id);
    if (filters.submitted_by_name) params.set("submitted_by_name", filters.submitted_by_name);
    if (filters.sla)               params.set("sla", filters.sla);
    const resp = await getData(`t2t/reports/tracker?${params.toString()}`);
    if (!resp.error) {
      const raw = resp.data?.data;
      setRequests(Array.isArray(raw?.rows) ? raw.rows : (Array.isArray(raw) ? raw : []));
    }
    setLoading(false);
  };

  // BUG-29 — show "Clear filters" link only when at least one non-default filter is set
  const hasActiveFilters = (
    filters.status || filters.workflow || filters.from || filters.to || filters.search ||
    filters.priority || filters.stage_id || filters.department_id || filters.submitted_by_name || filters.sla
  );
  const clearFilters = () => setFilters({
    status: "", workflow: "", from: "", to: "", search: "",
    priority: "", stage_id: "", department_id: "", submitted_by_name: "", sla: "",
  });

  const toggleSort = (col) => {
    if (sortBy === col) { setSortDir((d) => d === "asc" ? "desc" : "asc"); }
    else { setSortBy(col); setSortDir("asc"); }
  };

  const SortIcon = ({ col }) => sortBy === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const exportCsv = () => {
    if (requests.length === 0) return;
    const headers = ["Reference", "Title", "Workflow", "Stage", "Status", "Submitted By", "Date"];
    const rows = requests.map((r) => [r.request_id, r.title, r.workflow_name, r.current_stage, r.status, r.submitted_by, r.submitted_on]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "request_tracker.csv";
    a.click();
  };

  return (
    <>
      {/* Filter row 1 — primary filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input className="t2t-fc" style={{ flex: 2, minWidth: 180 }} placeholder="Search ref or title..." value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
        <select className="t2t-fc" style={{ flex: 1, minWidth: 120 }} value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All Statuses</option>
          <option value="in_progress">In Progress</option><option value="approved">Approved</option>
          <option value="rejected">Rejected</option><option value="withdrawn">Withdrawn</option>
        </select>
        <input className="t2t-fc" type="date" style={{ flex: 1, minWidth: 130 }} value={filters.from}
          onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
        <input className="t2t-fc" type="date" style={{ flex: 1, minWidth: 130 }} value={filters.to}
          onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
        <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={exportCsv}>↓ CSV</button>
      </div>

      {/* BUG-29 — Filter row 2: Priority / Workflow / Stage / Department / Submitted-by / SLA */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <select className="t2t-fc" style={{ flex: 1, minWidth: 120 }} value={filters.priority}
          onChange={(e) => setFilters({ ...filters, priority: e.target.value })}>
          <option value="">All Priorities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>

        <select className="t2t-fc" style={{ flex: 1, minWidth: 140 }} value={filters.workflow}
          onChange={(e) => setFilters({ ...filters, workflow: e.target.value, stage_id: "" })}>
          <option value="">All Workflows</option>
          {workflows.map((w) => (
            <option key={w.workflow_id} value={w.workflow_id}>{w.name || w.workflow_name}</option>
          ))}
        </select>

        <select className="t2t-fc" style={{ flex: 1, minWidth: 140 }} value={filters.stage_id}
          onChange={(e) => setFilters({ ...filters, stage_id: e.target.value })}
          disabled={!filters.workflow}
          title={!filters.workflow ? "Pick a workflow first to filter by stage" : ""}>
          <option value="">{filters.workflow ? "All Stages" : "Pick workflow first…"}</option>
          {stages.map((s) => (
            <option key={s.stage_id || s.id} value={s.stage_id || s.id}>{s.name}</option>
          ))}
        </select>

        <select className="t2t-fc" style={{ flex: 1, minWidth: 140 }} value={filters.department_id}
          onChange={(e) => setFilters({ ...filters, department_id: e.target.value })}>
          <option value="">All Departments</option>
          {departments.map((d) => (
            <option key={d.org_unit_id || d.id} value={d.org_unit_id || d.id}>{d.name}</option>
          ))}
        </select>

        <input className="t2t-fc" style={{ flex: 1, minWidth: 140 }} placeholder="Submitted by name..." value={filters.submitted_by_name}
          onChange={(e) => setFilters({ ...filters, submitted_by_name: e.target.value })} />

        <select className="t2t-fc" style={{ flex: 1, minWidth: 130 }} value={filters.sla}
          onChange={(e) => setFilters({ ...filters, sla: e.target.value })}>
          <option value="">All SLA</option>
          <option value="breached">SLA breached</option>
          <option value="on_track">SLA on track</option>
        </select>

        {hasActiveFilters && (
          <button
            className="t2t-btn t2t-btn-ghost t2t-btn-sm"
            style={{ color: "var(--t2t-text-muted)", fontSize: 11 }}
            onClick={clearFilters}
            title="Reset all filters"
          >
            ✕ Clear filters
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 32, color: "var(--t2t-text-muted)", fontSize: 12 }}>Loading...</div>
      ) : (
        <div className="t2t-table">
          <div className="t2t-table-head">
            <div style={{ cursor: "pointer" }} onClick={() => toggleSort("request_id")}>Ref<SortIcon col="request_id" /></div>
            <div>Title</div>
            <div>Workflow</div>
            <div>Stage</div>
            <div style={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>Status<SortIcon col="status" /></div>
            <div>Submitted By</div>
            <div style={{ cursor: "pointer" }} onClick={() => toggleSort("submitted_on")}>Date<SortIcon col="submitted_on" /></div>
          </div>
          {requests.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "var(--t2t-text-muted)", fontSize: 13 }}>No requests match filters.</div>
          ) : requests.map((r) => (
            <div
              className="t2t-table-row t2t-table-row-clickable"
              key={r.request_id}
              onClick={() => navigate(`/t2t-request/${r.request_id}`)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/t2t-request/${r.request_id}`); } }}
              role="button"
              tabIndex={0}
              style={{ cursor: "pointer" }}
              title="Open request detail"
            >
              <div style={{ fontWeight: 700, color: "var(--t2t-primary)", fontFamily: "monospace", fontSize: 11 }}>{r.request_id}</div>
              <div style={{ fontWeight: 600 }}>{r.title}</div>
              <div style={{ color: "var(--t2t-text-secondary)" }}>{r.workflow_name}</div>
              <div style={{ color: "var(--t2t-text-secondary)" }}>{r.current_stage}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-start" }}>
                <span className={`t2t-badge t2t-badge-${r.status}`}>{(r.status || "").replace("_", " ")}</span>
                {/* BUG-29: SLA breach + priority surfaced inline */}
                {r.sla_breached && (
                  <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 3, background: "#FEE2E2", color: "#991B1B", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                    SLA breached
                  </span>
                )}
                {r.priority && r.priority !== 'medium' && (
                  <span className={`t2t-badge t2t-badge-${r.priority}`} style={{ fontSize: 9, padding: "1px 6px" }}>
                    {r.priority}
                  </span>
                )}
              </div>
              <div style={{ color: "var(--t2t-text-secondary)" }}>{r.submitted_by}</div>
              <div style={{ color: "var(--t2t-text-muted)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                <span>{r.submitted_on}</span>
                <span aria-hidden="true" style={{ color: "var(--t2t-text-muted)", fontSize: 13, opacity: 0.6 }}>→</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE 3 — SLA Performance
// ═══════════════════════════════════════════════════════════════════════════════
const SLATab = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const resp = await getData("t2t/reports/sla");
      if (!resp.error) setData(resp.data?.data || {});
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: "var(--t2t-text-muted)", fontSize: 13 }}>Loading SLA report...</div>;
  if (!data) return <EmptyChart />;

  const compliance  = data.compliance_rate ?? "—";
  const breaches    = data.breaches || [];
  const stageAvg    = data.stage_averages || [];

  return (
    <>
      {/* Compliance Card */}
      <div className="t2t-stats-grid" style={{ marginBottom: 24 }}>
        <div className="t2t-stat-card">
          <div className="t2t-stat-label">SLA Compliance Rate (30d)</div>
          <div className="t2t-stat-value" style={{ color: compliance >= 80 ? "#36B82A" : compliance >= 50 ? "#D97706" : "#DC2626" }}>
            {typeof compliance === "number" ? `${compliance}%` : compliance}
          </div>
        </div>
        <div className="t2t-stat-card">
          <div className="t2t-stat-label">Total Breaches (30d)</div>
          <div className="t2t-stat-value" style={{ color: "#DC2626" }}>{data.total_breaches ?? "—"}</div>
        </div>
      </div>

      {/* Stage Average Duration */}
      {stageAvg.length > 0 && (
        <div className="t2t-card" style={{ padding: 18, marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Average Stage Duration</div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={stageAvg} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 11 }} label={{ value: "minutes", position: "insideBottomRight", fontSize: 10 }} />
              <YAxis dataKey="stage_name" type="category" width={140} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="avg_minutes" fill="#2563EB" />
              <Bar dataKey="sla_minutes" fill="#E2E8F0" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Breach Table */}
      {breaches.length > 0 && (
        <div className="t2t-card" style={{ padding: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>SLA Breach Details</div>
          <div className="t2t-table">
            <div className="t2t-table-head">
              <div>Stage</div><div>Workflow</div><div>Total Actions</div><div>Avg Minutes</div><div>Breaches</div><div>Breach Rate</div>
            </div>
            {breaches.map((b, i) => (
              <div className="t2t-table-row" key={i}>
                <div style={{ fontWeight: 600 }}>{b.stage_name}</div>
                <div style={{ color: "var(--t2t-text-secondary)" }}>{b.workflow_name}</div>
                <div>{b.total_actions}</div>
                <div>{b.avg_minutes ? Math.round(b.avg_minutes) : "—"}</div>
                <div style={{ color: "#DC2626", fontWeight: 700 }}>{b.breach_count}</div>
                <div style={{ color: b.breach_rate_pct > 30 ? "#DC2626" : "#D97706" }}>{b.breach_rate_pct}%</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE 4 — Workflow Analytics
// ═══════════════════════════════════════════════════════════════════════════════
const AnalyticsTab = () => {
  const [workflows, setWorkflows] = useState([]);
  const [selectedWf, setSelectedWf] = useState("");
  const [funnel, setFunnel]       = useState([]);
  const [rejections, setRejections] = useState([]);
  const [loading, setLoading]     = useState(false);

  useEffect(() => {
    getData("t2t/workflows?page=1&limit=100").then((resp) => {
      if (!resp.error) {
        // BUG-30 follow-up: backend returns { workflows, total, page, limit }.
        // Existing parser only checked .rows / .data — missed .workflows, so the
        // dropdown was silently empty even when workflows existed.
        const raw = resp.data?.data;
        const list = Array.isArray(raw) ? raw
          : Array.isArray(raw?.workflows) ? raw.workflows
          : Array.isArray(raw?.rows) ? raw.rows
          : Array.isArray(raw?.data) ? raw.data
          : [];
        setWorkflows(list);
        if (list.length > 0) setSelectedWf(String(list[0].workflow_id));
      }
    });
  }, []);

  useEffect(() => {
    if (!selectedWf) return;
    const fetch = async () => {
      setLoading(true);
      const [fResp, rResp] = await Promise.all([
        getData(`t2t/reports/analytics/funnel/${selectedWf}`),
        getData(`t2t/reports/analytics/rejections/${selectedWf}`),
      ]);
      if (!fResp.error) setFunnel(fResp.data?.data || []);
      if (!rResp.error) setRejections(rResp.data?.data || []);
      setLoading(false);
    };
    fetch();
  }, [selectedWf]);

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <select className="t2t-fc" style={{ maxWidth: 300 }} value={selectedWf} onChange={(e) => setSelectedWf(e.target.value)}>
          <option value="">Select workflow...</option>
          {workflows.map((w) => <option key={w.workflow_id} value={w.workflow_id}>{w.name}</option>)}
        </select>
      </div>

      {/* BUG-30: distinct empty state when no workflow is picked yet */}
      {!selectedWf ? (
        <div className="t2t-card" style={{ padding: 32, textAlign: "center", color: "var(--t2t-text-muted)" }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>📊</div>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: "var(--t2t-text)" }}>Pick a workflow above</div>
          <div style={{ fontSize: 12 }}>
            Once you choose one, you&apos;ll see its funnel breakdown and rejection rate by stage.
            {workflows.length === 0 && " (No active workflows found yet — create one in the Workflow Builder first.)"}
          </div>
        </div>
      ) : loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "var(--t2t-text-muted)", fontSize: 13 }}>Loading analytics...</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          {/* Workflow Funnel */}
          <div className="t2t-card" style={{ padding: 18 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Workflow Funnel</div>
            {funnel.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {funnel.map((stage, i) => {
                  const pct = funnel[0].count > 0 ? Math.round((stage.count / funnel[0].count) * 100) : 0;
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ width: 110, fontSize: 11, fontWeight: 600, textAlign: "right" }}>{stage.stage_name}</span>
                      <div style={{ flex: 1, background: "#F1F5F9", borderRadius: 4, height: 22, overflow: "hidden" }}>
                        <div style={{ width: `${pct}%`, background: COLORS[i % COLORS.length], height: "100%", borderRadius: 4, transition: "width 0.4s", minWidth: pct > 0 ? 20 : 0 }} />
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, width: 50 }}>{stage.count} ({pct}%)</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ padding: 20, textAlign: "center", color: "var(--t2t-text-muted)", fontSize: 12 }}>
                This workflow has no requests yet.
              </div>
            )}
          </div>

          {/* Rejection Heatmap */}
          <div className="t2t-card" style={{ padding: 18 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Rejection Rate by Stage</div>
            {rejections.length > 0 ? (
              <div className="t2t-table">
                <div className="t2t-table-head">
                  <div>Stage</div><div>Reviews</div><div>Rejections</div><div>Rate</div>
                </div>
                {rejections.map((r, i) => (
                  <div className="t2t-table-row" key={i}>
                    <div style={{ fontWeight: 600 }}>{r.stage_name}</div>
                    <div>{r.total_reviews}</div>
                    <div style={{ color: "#DC2626" }}>{r.rejection_count}</div>
                    <div style={{ fontWeight: 700, color: r.rejection_rate > 30 ? "#DC2626" : r.rejection_rate > 10 ? "#D97706" : "#36B82A" }}>
                      {r.rejection_rate}%
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: 20, textAlign: "center", color: "var(--t2t-text-muted)", fontSize: 12 }}>
                No rejections recorded for this workflow yet.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE 5 — User & Role Performance (BUG-32)
// ═══════════════════════════════════════════════════════════════════════════════
// BUG-32: threshold helpers shared between table colouring + "needs attention" flag.
const slaColor = (v) => v == null ? "var(--t2t-text-muted)" : v >= 90 ? "#16A34A" : v >= 70 ? "#D97706" : "#DC2626";
const rejColor = (v) => v == null ? "var(--t2t-text-muted)" : v <= 10 ? "#16A34A" : v <= 30 ? "#D97706" : "#DC2626";
const pendColor = (v) => v == null ? "var(--t2t-text-muted)" : v <= 5  ? "#16A34A" : v <= 15 ? "#D97706" : "#DC2626";
const countRed = (u) => {
  let r = 0;
  if (u.sla_compliance != null && u.sla_compliance < 70) r++;
  if (u.rejection_rate  != null && u.rejection_rate  > 30) r++;
  if (u.pending_count   != null && u.pending_count   > 15) r++;
  return r;
};
const fmtMin = (m) => {
  if (m == null) return "—";
  const n = Math.round(m);
  if (n < 60)   return `${n} min`;
  if (n < 1440) return `${(n / 60).toFixed(1)} h`;
  return `${(n / 1440).toFixed(1)} d`;
};

const ISO30dAgo = () => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

const UsersTab = ({ toast }) => {
  const [users,    setUsers]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [filters,  setFilters]  = useState({ from: ISO30dAgo(), to: "", role_id: "", workflow_id: "" });
  const [sortBy,   setSortBy]   = useState("tasks_completed");
  const [sortDir,  setSortDir]  = useState("desc");
  const [roles,    setRoles]    = useState([]);
  const [workflows,setWorkflows]= useState([]);

  useEffect(() => { loadLookups(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { loadUsers();   /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters]);

  const loadLookups = async () => {
    const [rResp, wResp] = await Promise.all([
      getData("t2t/roles?page=1&limit=100"),
      getData("t2t/workflows?page=1&limit=100"),
    ]);
    if (!rResp.error) {
      const raw = rResp.data?.data;
      setRoles(Array.isArray(raw) ? raw
        : Array.isArray(raw?.roles) ? raw.roles
        : Array.isArray(raw?.rows)  ? raw.rows
        : []);
    }
    if (!wResp.error) {
      const raw = wResp.data?.data;
      setWorkflows(Array.isArray(raw) ? raw
        : Array.isArray(raw?.workflows) ? raw.workflows
        : Array.isArray(raw?.rows) ? raw.rows
        : []);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.from)        params.set("from", filters.from);
    if (filters.to)          params.set("to",   filters.to);
    if (filters.role_id)     params.set("role_id",     filters.role_id);
    if (filters.workflow_id) params.set("workflow_id", filters.workflow_id);
    const resp = await getData(`t2t/reports/user-performance?${params.toString()}`);
    if (!resp.error) {
      const raw = resp.data?.data;
      const rows = Array.isArray(raw) ? raw
        : Array.isArray(raw?.rows) ? raw.rows
        : [];
      setUsers(rows);
    }
    setLoading(false);
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
  };
  const SortIcon = ({ col }) => sortBy === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  // Sort client-side so it's snappy
  const sortedUsers = [...users].sort((a, b) => {
    const av = a[sortBy] ?? -Infinity;
    const bv = b[sortBy] ?? -Infinity;
    if (typeof av === "string" || typeof bv === "string") {
      return sortDir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    }
    return sortDir === "asc" ? av - bv : bv - av;
  });

  // BUG-32 — insight strip computations
  const slowest = [...users]
    .filter((u) => u.avg_response_minutes != null && u.tasks_completed > 0)
    .sort((a, b) => (b.avg_response_minutes || 0) - (a.avg_response_minutes || 0))
    .slice(0, 3);
  const highReject = users
    .filter((u) => (u.rejection_rate || 0) > 30 && u.tasks_completed > 0)
    .sort((a, b) => (b.rejection_rate || 0) - (a.rejection_rate || 0))
    .slice(0, 3);
  const overloaded = [...users]
    .filter((u) => (u.pending_count || 0) > 0)
    .sort((a, b) => (b.pending_count || 0) - (a.pending_count || 0))
    .slice(0, 3);

  // BUG-32 — workload chart data (top 10 by pending desc)
  const workloadData = [...users]
    .filter((u) => (u.pending_count || 0) > 0)
    .sort((a, b) => (b.pending_count || 0) - (a.pending_count || 0))
    .slice(0, 10)
    .map((u) => ({ name: u.user_name, pending: u.pending_count }));

  const exportCsv = () => {
    if (sortedUsers.length === 0) return;
    const headers = ["User", "Role", "Tasks Completed", "Avg Response (min)", "SLA Compliance %", "Rejection Rate %", "Pending"];
    const rows = sortedUsers.map((u) => [u.user_name, u.role_name, u.tasks_completed, u.avg_response_minutes, u.sla_compliance, u.rejection_rate, u.pending_count]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "user_performance.csv";
    a.click();
    toast("CSV exported.", "success");
  };

  const hasActiveFilters = filters.from !== ISO30dAgo() || filters.to || filters.role_id || filters.workflow_id;
  const clearFilters = () => setFilters({ from: ISO30dAgo(), to: "", role_id: "", workflow_id: "" });

  return (
    <>
      {/* Filter strip */}
      <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 11, color: "var(--t2t-text-muted)", fontWeight: 600 }}>Period:</span>
        <input className="t2t-fc" type="date" style={{ flex: "0 1 150px" }} value={filters.from}
          onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
        <input className="t2t-fc" type="date" style={{ flex: "0 1 150px" }} value={filters.to}
          onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
        <select className="t2t-fc" style={{ flex: "0 1 160px" }} value={filters.role_id}
          onChange={(e) => setFilters({ ...filters, role_id: e.target.value })}>
          <option value="">All Roles</option>
          {roles.map((r) => <option key={r.role_id} value={r.role_id}>{r.name}</option>)}
        </select>
        <select className="t2t-fc" style={{ flex: "0 1 180px" }} value={filters.workflow_id}
          onChange={(e) => setFilters({ ...filters, workflow_id: e.target.value })}>
          <option value="">All Workflows</option>
          {workflows.map((w) => <option key={w.workflow_id} value={w.workflow_id}>{w.name || w.workflow_name}</option>)}
        </select>
        {hasActiveFilters && (
          <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ fontSize: 11, color: "var(--t2t-text-muted)" }} onClick={clearFilters}>
            ✕ Reset to last 30 days
          </button>
        )}
        <div style={{ marginLeft: "auto" }}>
          <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={exportCsv}>↓ Export CSV</button>
        </div>
      </div>

      {/* Needs Attention insight strip */}
      {!loading && users.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10, marginBottom: 16 }}>
          <InsightCard
            icon="🐢"
            title="Slowest responders"
            entries={slowest.map((u) => ({ name: u.user_name, sub: fmtMin(u.avg_response_minutes) }))}
            emptyText="No response time data in this period."
          />
          <InsightCard
            icon="⚠"
            title="High rejection rate"
            entries={highReject.map((u) => ({ name: u.user_name, sub: `${u.rejection_rate}%` }))}
            emptyText="Nobody above the 30% threshold. Good."
            tone={highReject.length > 0 ? "warning" : "ok"}
          />
          <InsightCard
            icon="📥"
            title="Overloaded — biggest pending queues"
            entries={overloaded.map((u) => ({ name: u.user_name, sub: `${u.pending_count} pending` }))}
            emptyText="Nobody has pending requests right now."
            tone={overloaded.some((u) => u.pending_count > 15) ? "danger" : "neutral"}
          />
        </div>
      )}

      {/* Workload chart */}
      {!loading && workloadData.length > 0 && (
        <div className="t2t-card" style={{ padding: 14, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Workload distribution — pending per user (top 10)</div>
          <ResponsiveContainer width="100%" height={Math.max(120, workloadData.length * 32)}>
            <BarChart data={workloadData} layout="vertical" margin={{ top: 4, right: 24, left: 80, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
              <XAxis type="number" allowDecimals={false} style={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={120} style={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => `${v} pending`} />
              <Bar dataKey="pending" radius={[0, 4, 4, 0]}>
                {workloadData.map((d, i) => (
                  <Cell key={i} fill={d.pending > 15 ? "#DC2626" : d.pending > 5 ? "#D97706" : "#36B82A"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "var(--t2t-text-muted)", fontSize: 13 }}>Loading...</div>
      ) : (
        <div className="t2t-table">
          <div className="t2t-table-head">
            <div style={{ cursor: "pointer" }} onClick={() => toggleSort("user_name")}>User<SortIcon col="user_name" /></div>
            <div style={{ cursor: "pointer" }} onClick={() => toggleSort("role_name")}>Role<SortIcon col="role_name" /></div>
            <div style={{ cursor: "pointer" }} onClick={() => toggleSort("tasks_completed")}>Tasks Done<SortIcon col="tasks_completed" /></div>
            <div style={{ cursor: "pointer" }} onClick={() => toggleSort("avg_response_minutes")}>Avg Response<SortIcon col="avg_response_minutes" /></div>
            <div style={{ cursor: "pointer" }} onClick={() => toggleSort("sla_compliance")}>SLA %<SortIcon col="sla_compliance" /></div>
            <div style={{ cursor: "pointer" }} onClick={() => toggleSort("rejection_rate")}>Reject %<SortIcon col="rejection_rate" /></div>
            <div style={{ cursor: "pointer" }} onClick={() => toggleSort("pending_count")}>Pending<SortIcon col="pending_count" /></div>
          </div>
          {sortedUsers.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "var(--t2t-text-muted)", fontSize: 13 }}>
              No user activity in the selected period.
            </div>
          ) : sortedUsers.map((u, i) => {
            const reds = countRed(u);
            return (
              <div className="t2t-table-row" key={u.user_id || i}>
                <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                  {reds >= 2 && <span title={`${reds} thresholds in the red`} aria-label={`Needs attention: ${reds} thresholds in red`}>🚩</span>}
                  {u.user_name}
                </div>
                <div style={{ color: "var(--t2t-text-secondary)" }}>{u.role_name || "—"}</div>
                <div>{u.tasks_completed ?? 0}</div>
                <div>{fmtMin(u.avg_response_minutes)}</div>
                <div style={{ color: slaColor(u.sla_compliance), fontWeight: 700 }}>
                  {u.sla_compliance != null ? `${u.sla_compliance}%` : "—"}
                </div>
                <div style={{ color: rejColor(u.rejection_rate), fontWeight: 700 }}>
                  {u.rejection_rate != null ? `${u.rejection_rate}%` : "—"}
                </div>
                <div style={{ color: pendColor(u.pending_count), fontWeight: 700 }}>
                  {u.pending_count ?? 0}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

// BUG-32 — small insight card for the "Needs Attention" strip
const InsightCard = ({ icon, title, entries, emptyText, tone = "neutral" }) => {
  const bg     = tone === "danger" ? "#FEF2F2" : tone === "warning" ? "#FFFBEB" : tone === "ok" ? "#F0FDF4" : "#F8FAFC";
  const border = tone === "danger" ? "#FECACA" : tone === "warning" ? "#FDE68A" : tone === "ok" ? "#BBF7D0" : "#E2E8F0";
  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: "12px 14px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--t2t-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 8 }}>
        <span style={{ marginRight: 6 }}>{icon}</span>{title}
      </div>
      {entries.length === 0 ? (
        <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", fontStyle: "italic" }}>{emptyText}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {entries.map((e, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
              <span style={{ fontWeight: 600 }}>{e.name}</span>
              <span style={{ color: "var(--t2t-text-secondary)", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{e.sub}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
InsightCard.propTypes = {
  icon:      PropTypes.string.isRequired,
  title:     PropTypes.string.isRequired,
  entries:   PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, sub: PropTypes.string })).isRequired,
  emptyText: PropTypes.string.isRequired,
  tone:      PropTypes.oneOf(["neutral", "ok", "warning", "danger"]),
};

OverviewTab.propTypes = { toast: PropTypes.func };
UsersTab.propTypes    = { toast: PropTypes.func };

// ── Shared ──
const EmptyChart = () => (
  <div style={{ textAlign: "center", padding: 40, color: "var(--t2t-text-muted)", fontSize: 12 }}>
    No data available yet.
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE 6 — Integration Health (BUG-31)
// Renders three sections per Integration Specification §7:
//   (1) Health cards — one per configured integration
//   (2) Unified call log table — filterable, retry per row
//   (3) Cost tracker — monthly verification API spend
// All endpoints already exist; this tab is purely presentational.
// ═══════════════════════════════════════════════════════════════════════════════
const INTEGRATION_LABEL = {
  sap:          "SAP Master Data Push",
  ariba:        "Ariba Procurement",
  fiori:        "Fiori SSO",
  pan_validate: "PAN Verification",
  gst_validate: "GST Verification",
  bank_verify:  "Bank Account Verification",
  pan:          "PAN Verification",
  gst:          "GST Verification",
  bank:         "Bank Account Verification",
  email:        "Email (SMTP)",
  whatsapp:     "WhatsApp (WATI)",
  sms:          "SMS",
  einvoice:     "e-Invoice",
  gem:          "GeM",
  treds:        "TReDS",
};
const HEALTH_STATE = {
  healthy:      { color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0", label: "Healthy" },
  degraded:     { color: "#D97706", bg: "#FFFBEB", border: "#FDE68A", label: "Degraded" },
  down:         { color: "#DC2626", bg: "#FEF2F2", border: "#FECACA", label: "Down" },
  unconfigured: { color: "#94A3B8", bg: "#F8FAFC", border: "#E2E8F0", label: "Unconfigured" },
};
const callStatusStyle = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "success" || s === "verified")             return { color: "#16A34A", bg: "#F0FDF4" };
  if (s === "failed" || s === "exhausted" || s === "rejected") return { color: "#DC2626", bg: "#FEF2F2" };
  if (s === "retrying" || s === "running" || s === "pending")  return { color: "#D97706", bg: "#FFFBEB" };
  if (s === "timeout")                                  return { color: "#7C3AED", bg: "#F5F3FF" };
  return { color: "#64748B", bg: "#F1F5F9" };
};
const fmtTimestamp = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};
const fmtDuration = (ms) => {
  if (ms == null || ms === "" || isNaN(Number(ms))) return "—";
  const n = Math.abs(Number(ms));
  if (n < 1000)  return `${n}ms`;
  return `${(n / 1000).toFixed(1)}s`;
};

const IntegrationHealthTab = ({ toast }) => {
  const navigate = useNavigate();
  const [health,    setHealth]    = useState([]);
  const [callLog,   setCallLog]   = useState([]);
  const [cost,      setCost]      = useState({ month: "", entries: [], total_calls: 0 });
  const [loading,   setLoading]   = useState(false);
  const [retrying,  setRetrying]  = useState({}); // {logId: true}
  const [filters,   setFilters]   = useState({ type: "", status: "", date_from: "", date_to: "" });
  const [costMonth, setCostMonth] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => { loadAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { loadCallLog(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters]);
  useEffect(() => { loadCost();    /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [costMonth]);

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadHealth(), loadCallLog(), loadCost()]);
    setLoading(false);
  };
  const loadHealth = async () => {
    const r = await fetchIntegrationHealth();
    if (!r.error) setHealth(r.data?.data || []);
  };
  const loadCallLog = async () => {
    const r = await fetchIntegrationCallLog(filters);
    if (!r.error) setCallLog(r.data?.data || []);
  };
  const loadCost = async () => {
    const r = await fetchIntegrationCostTracker(costMonth);
    if (!r.error) setCost(r.data?.data || { month: costMonth, entries: [], total_calls: 0 });
  };

  const onRetry = async (logId) => {
    setRetrying((p) => ({ ...p, [logId]: true }));
    const r = await retryIntegrationCall(logId);
    setRetrying((p) => { const n = { ...p }; delete n[logId]; return n; });
    if (r.error) toast(r.error.message || "Retry failed.", "error");
    else         { toast("Retry queued.", "success"); loadCallLog(); }
  };

  const filterToType = (type) => setFilters({ ...filters, type });

  // Aggregate counts for the strip above the call log
  const counts = callLog.reduce((acc, row) => {
    const s = (row.status || "").toLowerCase();
    acc.total++;
    if (s === "success" || s === "verified")                                       acc.success++;
    else if (s === "failed" || s === "exhausted" || s === "rejected")              acc.failed++;
    else if (s === "retrying" || s === "running" || s === "pending")               acc.retrying++;
    return acc;
  }, { total: 0, success: 0, failed: 0, retrying: 0 });

  return (
    <>
      <p style={{ fontSize: 12, color: "var(--t2t-text-secondary)", marginBottom: 16 }}>
        Live view of every outbound integration — SAP, Ariba, PAN / GST / Bank verification, e-Invoice, GeM, TReDS, plus email and WhatsApp delivery. Health pings run every 15 minutes; the call log shows individual calls with status, duration, and a manual retry for failed ones.
      </p>

      {/* ── Section 1: Health cards ── */}
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Health Status</div>
      {health.length === 0 ? (
        <div style={{ padding: 18, fontSize: 12, color: "var(--t2t-text-muted)", border: "1px dashed var(--t2t-border)", borderRadius: 8, marginBottom: 22, textAlign: "center" }}>
          No integrations configured yet. Configure providers in Admin → Channels and Admin → Integrations.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10, marginBottom: 22 }}>
          {health.map((h) => {
            const state = HEALTH_STATE[h.status] || HEALTH_STATE.unconfigured;
            const label = INTEGRATION_LABEL[h.integration_type] || h.integration_type;
            return (
              <button
                type="button"
                key={h.integration_type}
                onClick={() => filterToType(h.integration_type)}
                style={{
                  background: state.bg, border: `1px solid ${state.border}`,
                  borderRadius: 8, padding: "12px 14px", textAlign: "left",
                  cursor: "pointer", transition: "transform 0.12s",
                }}
                title={`Click to filter the call log to ${label}`}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: state.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: state.color, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    {state.label}
                  </span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 10, color: "var(--t2t-text-muted)", display: "flex", flexDirection: "column", gap: 2 }}>
                  <span>Last checked: {fmtTimestamp(h.last_checked)}</span>
                  {h.response_ms != null && <span>Avg response: {fmtDuration(h.response_ms)}</span>}
                  {h.error_message && <span style={{ color: "#DC2626", wordBreak: "break-word" }} title={h.error_message}>
                    {h.error_message.slice(0, 40)}{h.error_message.length > 40 ? "…" : ""}
                  </span>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Section 2: Unified call log ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 13 }}>Call Log</div>
        <div style={{ display: "flex", gap: 6, fontSize: 11, alignItems: "center" }}>
          <span style={{ color: "#64748B" }}>{counts.total} shown · </span>
          <span style={{ color: "#16A34A", fontWeight: 600 }}>{counts.success} success</span>
          <span style={{ color: "#DC2626", fontWeight: 600 }}>· {counts.failed} failed</span>
          <span style={{ color: "#D97706", fontWeight: 600 }}>· {counts.retrying} retrying</span>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        <select className="t2t-fc" style={{ minWidth: 160, flex: "0 1 200px" }}
          value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
          <option value="">All Integrations</option>
          {[...new Set(health.map((h) => h.integration_type))].map((t) => (
            <option key={t} value={t}>{INTEGRATION_LABEL[t] || t}</option>
          ))}
        </select>
        <select className="t2t-fc" style={{ minWidth: 130, flex: "0 1 160px" }}
          value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All Statuses</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="retrying">Retrying</option>
          <option value="timeout">Timeout</option>
          <option value="exhausted">Exhausted</option>
        </select>
        <input className="t2t-fc" type="date" style={{ flex: "0 1 150px" }} value={filters.date_from}
          onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
        <input className="t2t-fc" type="date" style={{ flex: "0 1 150px" }} value={filters.date_to}
          onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
        {(filters.type || filters.status || filters.date_from || filters.date_to) && (
          <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ fontSize: 11, color: "var(--t2t-text-muted)" }}
            onClick={() => setFilters({ type: "", status: "", date_from: "", date_to: "" })}>
            ✕ Clear
          </button>
        )}
      </div>

      <div style={{ border: "1px solid var(--t2t-border)", borderRadius: 8, overflow: "hidden", marginBottom: 22 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#F8FAFC", borderBottom: "1px solid var(--t2t-border)" }}>
              <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#475569", fontSize: 11 }}>Timestamp</th>
              <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#475569", fontSize: 11 }}>Integration</th>
              <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#475569", fontSize: 11 }}>Action</th>
              <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#475569", fontSize: 11 }}>Request</th>
              <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#475569", fontSize: 11 }}>Status</th>
              <th style={{ padding: "8px 12px", textAlign: "right", fontWeight: 600, color: "#475569", fontSize: 11 }}>Duration</th>
              <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#475569", fontSize: 11 }}>Error</th>
              <th style={{ padding: "8px 12px", textAlign: "center", fontWeight: 600, color: "#475569", fontSize: 11 }} aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: 32, textAlign: "center", color: "var(--t2t-text-muted)", fontSize: 12 }}>Loading…</td></tr>
            ) : callLog.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 32, textAlign: "center", color: "var(--t2t-text-muted)", fontSize: 12 }}>
                No integration calls match the current filters.
              </td></tr>
            ) : (
              callLog.map((row) => {
                const ss = callStatusStyle(row.status);
                const failedOrTimeout = ["failed", "timeout", "exhausted"].includes((row.status || "").toLowerCase());
                return (
                  <tr key={row.id} style={{ borderTop: "1px solid #F1F5F9" }}>
                    <td style={{ padding: "7px 12px", whiteSpace: "nowrap", color: "var(--t2t-text-secondary)" }}>{fmtTimestamp(row.created_at)}</td>
                    <td style={{ padding: "7px 12px", fontWeight: 600 }}>{INTEGRATION_LABEL[row.integration_type] || row.integration_type}</td>
                    <td style={{ padding: "7px 12px", color: "var(--t2t-text-secondary)" }}>{row.action || "—"}</td>
                    <td style={{ padding: "7px 12px" }}>
                      {row.request_id ? (
                        <button type="button"
                          onClick={() => navigate(`/t2t-request/${row.request_id}`)}
                          style={{ background: "none", border: "none", padding: 0, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", color: "var(--t2t-primary)", cursor: "pointer", fontWeight: 700, fontSize: 11 }}>
                          REQ-{String(row.request_id).padStart(3, "0")}
                        </button>
                      ) : <span style={{ color: "#CBD5E1" }}>—</span>}
                    </td>
                    <td style={{ padding: "7px 12px" }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
                        background: ss.bg, color: ss.color, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        {row.status || "unknown"}
                      </span>
                    </td>
                    <td style={{ padding: "7px 12px", textAlign: "right", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", color: "var(--t2t-text-secondary)" }}>{fmtDuration(row.duration_ms)}</td>
                    <td style={{ padding: "7px 12px", color: "#DC2626", fontSize: 11, maxWidth: 240, wordBreak: "break-word" }} title={row.error_message || ""}>
                      {row.error_message ? `${row.error_message.slice(0, 60)}${row.error_message.length > 60 ? "…" : ""}` : ""}
                    </td>
                    <td style={{ padding: "7px 12px", textAlign: "center" }}>
                      {failedOrTimeout && (
                        <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ fontSize: 11 }}
                          onClick={() => onRetry(row.id)} disabled={!!retrying[row.id]}>
                          {retrying[row.id] ? "…" : "↻ Retry"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Section 3: Cost tracker ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 13 }}>Cost Tracker — Verification APIs</div>
        <input className="t2t-fc" type="month" style={{ maxWidth: 180 }}
          value={costMonth} onChange={(e) => setCostMonth(e.target.value)} />
      </div>

      {cost.entries.length === 0 ? (
        <div style={{ padding: 18, fontSize: 12, color: "var(--t2t-text-muted)", border: "1px dashed var(--t2t-border)", borderRadius: 8, textAlign: "center" }}>
          No verification calls recorded for {cost.month || costMonth}.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
          {cost.entries.map((e, i) => {
            const successRate = parseInt(e.total_calls) > 0
              ? Math.round((parseInt(e.successful_calls) / parseInt(e.total_calls)) * 100)
              : 0;
            return (
              <div key={i} style={{ border: "1px solid var(--t2t-border)", borderRadius: 8, padding: 14, background: "#fff" }}>
                <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>
                  {INTEGRATION_LABEL[e.verification_type] || e.verification_type}
                </div>
                <div style={{ fontSize: 11, color: "var(--t2t-text-secondary)", marginBottom: 8 }}>via {e.provider}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "var(--t2t-text)", marginBottom: 4 }}>{e.total_calls}</div>
                <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginBottom: 6 }}>calls in {cost.month || costMonth}</div>
                <div style={{ display: "flex", gap: 8, fontSize: 11 }}>
                  <span style={{ color: "#16A34A", fontWeight: 600 }}>{e.successful_calls} ✓</span>
                  {parseInt(e.failed_calls) > 0 && <span style={{ color: "#DC2626", fontWeight: 600 }}>{e.failed_calls} ✗</span>}
                  <span style={{ color: "var(--t2t-text-muted)", marginLeft: "auto" }}>{successRate}% success</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};
IntegrationHealthTab.propTypes = { toast: PropTypes.func.isRequired };

export default T2TReports;
